// Eager global error handlers.
// Sentry.init() is deferred until requestIdleCallback (see main.tsx + init.ts),
// so the SDK's own window.onerror / unhandledrejection handlers aren't installed
// during first load. Async errors thrown before init — e.g. mapbox-gl rejecting
// on an invalid token — would otherwise be lost (the ErrorBoundary only catches
// React render errors). These eager listeners feed such errors into the buffer
// (src/sentry/buffer.ts) so they're reported once the real SDK drains it.
//
// Once Sentry binds (init.ts → bindRealSentry), we remove these listeners and
// let the SDK's globalHandlersIntegration take over, avoiding duplicate reports.

import { captureException } from './buffer'

let detach: (() => void) | null = null

export function installEagerGlobalHandlers(): void {
  if (detach) return

  const onError = (event: ErrorEvent): void => {
    captureException(event.error ?? event.message)
  }
  const onRejection = (event: PromiseRejectionEvent): void => {
    captureException(event.reason)
  }

  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)

  detach = () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}

export function removeEagerGlobalHandlers(): void {
  detach?.()
  detach = null
}
